import { Link } from "react-router-dom";
import { MaskedLines, Reveal } from "../components/Reveal";
import { StandardMark } from "./Certifications";
import { STANDARDS } from "../data/site";
import { WALL } from "../data/clients";

// Proof & Standards — one connected section: header on the shared grid,
// a controlled static logo wall (grey at rest, brand colour on hover),
// the clients CTA under the wall, then a bridge into the standards band.
export function ClientProof() {
  return (
    <section className="bg-white py-16 md:py-24" aria-label="Client proof and standards">
      <div className="container-x">
        <Reveal>
          <p className="eyebrow">05 / Proven in operating facilities</p>
        </Reveal>
        <div className="mt-6 grid gap-8 lg:grid-cols-12 lg:items-end">
          <MaskedLines
            as="h2"
            lines={["TRUSTED BY LEADING", "COMMERCIAL FACILITIES."]}
            accent={[1]}
            accentTone="text-dcc-strong"
            className="h-display text-4xl text-charcoal sm:text-5xl lg:col-span-8 lg:text-6xl"
          />
          <Reveal delay={0.15} className="lg:col-span-4">
            <p className="max-w-md text-sm leading-relaxed text-zinc-600 md:text-base">
              DCC supports leading hospitality brands, workplaces and commercial facilities across India with specialist duct, exhaust and hygiene-system services.
            </p>
          </Reveal>
        </div>

        {/* moving logo rail — equal cells, grey at rest, brand colour on hover;
            pauses on hover, static wrapped grid under reduced motion */}
        <div className="rail rail-mask mt-10" data-testid="client-logo-wall" aria-label="Selected DCC clients">
          <div className="rail-track animate-marquee">
            {[0, 1].map((copy) => (
              <ul
                key={copy}
                aria-hidden={copy === 1 || undefined}
                ref={copy === 1 ? (el) => { if (el) el.setAttribute("inert", ""); } : undefined}
                className="rail-copy gap-3 pr-3"
              >
                {WALL.map((l) => (
                  <li key={l.slug}>
                    <div
                      className="rail-logo flex h-24 w-[200px] items-center justify-center border border-zinc-200 bg-white px-6 md:h-28 md:w-[230px]"
                      data-testid={copy === 0 ? `wall-logo-${l.slug}` : undefined}
                    >
                      <img
                        src={l.src}
                        alt={copy === 0 ? `${l.name} logo` : ""}
                        loading="lazy" decoding="async" width="200" height="80"
                        className="max-h-10 w-auto max-w-full object-contain opacity-60 grayscale transition-[opacity,filter] duration-500 hover:opacity-100 hover:grayscale-0 md:max-h-12"
                      />
                    </div>
                  </li>
                ))}
              </ul>
            ))}
          </div>
        </div>

        <Reveal delay={0.1}>
          <Link to="/clients" data-testid="proof-clients-link" data-cursor="OPEN" className="link-tech mt-8 inline-flex text-charcoal">
            View all clients <span aria-hidden="true" className="text-dcc-strong">→</span>
          </Link>
        </Reveal>

        {/* bridge into standards — connected second layer */}
        <div className="mt-16 border border-zinc-200 bg-paper p-6 md:p-10" data-testid="standards-band">
          <div className="grid gap-6 lg:grid-cols-12 lg:items-end">
            <Reveal className="lg:col-span-7">
              <h3 className="h-display text-3xl text-charcoal sm:text-4xl">
                CLIENT TRUST. <span className="text-dcc-strong">DOCUMENTED STANDARDS.</span>
              </h3>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-5">
              <p className="max-w-md text-sm leading-relaxed text-zinc-600">
                Every assignment follows a defined survey, execution, verification and handover process — guided by relevant industry standards and facility requirements.
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.05}>
            <p className="mt-10 flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
              Standards &amp; industry frameworks
              <span className="h-px flex-1 bg-zinc-300" aria-hidden="true" />
            </p>
          </Reveal>

          <div className="mt-6 grid grid-cols-1 gap-px border border-zinc-200 bg-zinc-200 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {STANDARDS.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.05} className="h-full">
                {/* equal-height card: mark → acronym → full name → badge pinned to one bottom baseline */}
                <div className="flex h-full min-h-[220px] flex-col bg-white p-6" data-testid={`standard-${s.id}`}>
                  <div className="flex h-12 items-center">
                    <StandardMark s={s} />
                  </div>
                  <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-dcc-strong">{s.short}</p>
                  <p className="mt-2 text-sm leading-snug text-zinc-600">{s.cardName || s.name}</p>
                  <p className="mt-auto pt-5">
                    <span className="inline-block border border-dcc/40 px-2 py-1 font-mono text-xs uppercase tracking-[0.2em] text-dcc-strong">{s.relation}</span>
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
